import { Alert, Anchor, Badge, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { listCatalogs, type CatalogSummary } from "@/api/catalogs";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";

/** The detail-page path for a catalog, or null for the global tier (no org key). */
function detailPath(c: CatalogSummary): string | null {
  if (c.org_id === null) return null;
  return c.branch_id !== null
    ? `/catalogs/${encodeURIComponent(c.org_id)}/branch/${encodeURIComponent(c.branch_id)}`
    : `/catalogs/${encodeURIComponent(c.org_id)}`;
}

/**
 * Phase 3 (ADR-0040 read surfaces) — the catalog list.
 *
 * Calls `GET /api/v1/catalogs` and renders the visible catalogs as a table. This
 * is the entry point for the read surfaces; catalog detail
 * (`/catalogs/:org`) + the biomarker explorer chain off it in the next slices.
 * Backend enforces `catalog:read`; the route wraps `<Authenticated>` in App.tsx.
 */

const _TIER_COLOR: Record<CatalogSummary["tier"], string> = {
  global: "blue",
  tenant: "teal",
  branch: "grape",
};

export function CatalogListPage(): JSX.Element {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["catalogs"],
    queryFn: listCatalogs,
  });

  return (
    <AppLayout breadcrumbs={[{ label: t("catalogs.title") }]}>
      <Title order={1}>{t("catalogs.title")}</Title>
      {isPending ? (
        <TableSkeleton label={t("catalogs.loading")} />
      ) : isError ? (
        <Alert color="red" title={t("catalogs.error_title")}>
          {error instanceof Error ? error.message : t("catalogs.error_generic")}
        </Alert>
      ) : data.catalogs.length === 0 ? (
        <Text c="dimmed">{t("catalogs.empty")}</Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("catalogs.col_tier")}</Table.Th>
              <Table.Th>{t("catalogs.col_org")}</Table.Th>
              <Table.Th>{t("catalogs.col_branch")}</Table.Th>
              <Table.Th>{t("catalogs.col_biomarkers")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.catalogs.map((c) => {
              const path = detailPath(c);
              return (
                <Table.Tr key={`${c.tier}:${c.org_id ?? "-"}:${c.branch_id ?? "-"}`}>
                  <Table.Td>
                    <Badge color={_TIER_COLOR[c.tier]} variant="light">
                      {c.tier}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {path !== null ? (
                      <Anchor component={Link} to={path}>
                        {c.org_id}
                      </Anchor>
                    ) : (
                      "—"
                    )}
                  </Table.Td>
                  <Table.Td>{c.branch_id ?? "—"}</Table.Td>
                  <Table.Td>{c.biomarker_count}</Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}
    </AppLayout>
  );
}
